/**
 * Milestone Verification Dialog
 * Dialog for verifying achieved equity milestones
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { GphtlEquityMilestone } from '../../../../../types/gphtl.types';
import { useVerifyMilestone } from '../../../../../hooks/useGphtl';

// Format currency for display
const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

interface MilestoneVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  milestone: GphtlEquityMilestone | null;
  onSuccess?: () => void;
}

export const MilestoneVerificationDialog: React.FC<MilestoneVerificationDialogProps> = ({
  open,
  onClose,
  milestone,
  onSuccess,
}) => {
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const verifyMutation = useVerifyMilestone();

  const handleVerify = async () => {
    if (!milestone) return;

    setError(null);
    try {
      await verifyMutation.mutateAsync({
        milestoneId: milestone.milestone_id,
        notes: notes.trim() || undefined,
      });

      // Success - close dialog and notify parent
      setNotes('');
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify milestone');
    }
  };

  const handleClose = () => {
    setNotes('');
    setError(null);
    onClose();
  };

  if (!milestone) return null;

  const targetValue = milestone.revenue_threshold ?? 0;
  const equityPercentage = milestone.equity_percentage ?? 0;
  const isAchieved = milestone.status === 'ACHIEVED';

  // Determine requirements label based on milestone type
  const getRequirementsLabel = (): string => {
    switch (milestone.milestone_type) {
      case 'CLIFF':
        return `Revenue threshold (${formatCurrency(targetValue)}) reached`;
      case 'TIME_BASED':
        return `Time requirement (${milestone.time_requirement_months} months) met`;
      case 'PERFORMANCE':
        return `Revenue milestone (${formatCurrency(targetValue)}) achieved`;
      case 'BUYOUT_LINKED':
        return `Buyout-linked requirement met`;
      default:
        return `Requirement met`;
    }
  };

  // Build requirements met list
  const requirementsMet = [
    {
      label: getRequirementsLabel(),
      met: isAchieved,
    },
  ];

  // Add annual revenue requirement for time-based milestones
  if (milestone.milestone_type === 'TIME_BASED') {
    requirementsMet.push({
      label: `Annual revenue requirement ($1M) met`,
      met: isAchieved, // If milestone shows as achieved, this was verified
    });
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VerifiedIcon color="primary" />
          Verify Milestone Achievement
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {milestone.milestone_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {milestone.description}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Requirements Verified
        </Typography>
        <List dense>
          {requirementsMet.map((req, index) => (
            <ListItem key={index}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color={req.met ? 'success' : 'disabled'} fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={req.label} />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Equity to be Vested
          </Typography>
          <Typography variant="h4" color="primary">
            {equityPercentage}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            of Seaplane Ventures LLC ownership
          </Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Verification Notes (Optional)"
          placeholder="Add any relevant notes about this verification..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={verifyMutation.isPending}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={verifyMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleVerify}
          disabled={verifyMutation.isPending}
          startIcon={verifyMutation.isPending ? <CircularProgress size={16} /> : <VerifiedIcon />}
        >
          {verifyMutation.isPending ? 'Verifying...' : 'Verify Milestone'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MilestoneVerificationDialog;
